import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { COLOR_SYSTEM, ColorSystem } from "components/design/design-system";
import { startKakaoAuthorize } from "utils/kakaoAuth";
import { requestDesktopHandoff } from "api/desktopAuthService";
import { log, LogLevel } from "utils/LogUtil";

/**
 * `/desktop-auth` — 데스크톱 인증 핸드오프 페이지 (admin-frontend spec §6, 계약 §3.3.3/§3.3.5)
 *
 * PhAI 약국 데스크톱 앱은 카카오와 직접 대화할 수 없어, 웹이 인증을 대행하고 앱에 세션을 넘긴다.
 * 이 페이지는 기존 로그인·가입 화면을 **고치지 않고** 진입·완료를 감싸는 얇은 레이어다.
 *
 * 동작:
 *  1. 앱이 `/desktop-auth?intent=login|signup&cb=http://127.0.0.1:<port>/cb&state=<nonce>`로 브라우저를 연다.
 *  2. `cb`·`state`를 sessionStorage에, `localStorage.authRedirect='/desktop-auth'`를 심고
 *     intent에 따라 기존 `/login` 또는 기존 카카오 가입 흐름으로 위임한다.
 *  3. 인증이 끝나 이 페이지로 자동 복귀하면(로그인 화면이 authRedirect로 되돌린다)
 *     `POST /auth/desktop/handoff`로 일회용 코드를 받는다.
 *  4. `cb`로 code·state를 전달한다(숨김 iframe — 상단 페이지는 유지).
 *  5. 전달이 막히면(방화벽 등) 코드를 화면에 크게 표시하고 앱에 붙여넣도록 안내한다(필수 폴백).
 *
 * ⚠️ 이 페이지는 App.tsx의 **최상위 공개 라우트**(AuthInitializer·인터셉터 리다이렉트 밖)에 둔다.
 *    AuthInitializer는 인증 확인이 끝날 때까지 자식을 렌더하지 않고, axiosInstance 인터셉터는
 *    비공개 경로 401 시 `/login`으로 하드 리다이렉트한다. AuthenticatedApp 안에 두면 비로그인
 *    초기 진입에서 이 컴포넌트가 마운트되기도 전에 튕겨나가 cb·state 저장 기회를 잃는다.
 */

const AUTH_REDIRECT_PATH = "/desktop-auth";
const SS_CB_KEY = "desktopAuthCb";
const SS_STATE_KEY = "desktopAuthState";

type Phase =
  | { kind: "init" }
  | { kind: "redirecting" }
  | { kind: "signupNotice" }
  | { kind: "handoff" }
  | { kind: "code"; code: string; cbUrl: string }
  | { kind: "error"; message: string; onRetry?: () => void };

const color = (name: ColorSystem, shade: number): string =>
  ((COLOR_SYSTEM.get(name) as unknown) as Record<number, string> | undefined)?.[shade] ?? "#000000";

/**
 * cb는 loopback 주소만 허용한다(규칙 1 — 서버도 검증하지만 웹에서도 막는 이중 방어).
 * open redirect로 코드가 외부 사이트에 유출되는 것을 차단한다.
 */
const isLoopbackCallback = (raw: string): boolean => {
  try {
    const u = new URL(raw);
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    const host = u.hostname;
    return host === "127.0.0.1" || host === "localhost" || host === "::1";
  } catch {
    return false;
  }
};

/** cb 주소에 code·state를 실어 앱 수신 URL을 만든다(state는 그대로 되돌려준다 — 규칙 2). */
const buildCbUrl = (cb: string, code: string, state: string | null): string => {
  const u = new URL(cb);
  u.searchParams.set("code", code);
  if (state) u.searchParams.set("state", state);
  return u.toString();
};

const DesktopAuth: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [phase, setPhase] = useState<Phase>({ kind: "init" });
  const [copied, setCopied] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const didInit = useRef(false);

  const runHandoff = useCallback(async (cb: string, state: string | null) => {
    setPhase({ kind: "handoff" });
    try {
      const { code } = await requestDesktopHandoff(cb);
      setPhase({ kind: "code", code, cbUrl: buildCbUrl(cb, code, state) });
    } catch (error) {
      // 백엔드 부재(404/5xx)·네트워크 오류·미인증 모두 여기로 온다. 화면이 죽지 않고 안내를 띄운다.
      log(LogLevel.ERROR, "데스크톱 핸드오프 실패:", error);
      setPhase({
        kind: "error",
        message: "인증 서버에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.",
        onRetry: () => runHandoff(cb, state),
      });
    }
  }, []);

  const startSignup = useCallback(() => {
    try {
      // 기존 카카오 가입 흐름으로 위임한다(utils/kakaoAuth는 고치지 않고 호출만 한다).
      startKakaoAuthorize("signup-ymyd");
    } catch (error) {
      log(LogLevel.ERROR, "카카오 가입 시작 실패:", error);
      setPhase({
        kind: "error",
        message: "카카오 인증을 시작하지 못했습니다. 잠시 후 다시 시도해 주세요.",
        onRetry: startSignup,
      });
    }
  }, []);

  useEffect(() => {
    // StrictMode(dev) 이중 실행 및 재렌더 재진입 방지 — 진입 로직은 1회만.
    if (didInit.current) return;
    didInit.current = true;

    const urlCb = searchParams.get("cb");
    const urlState = searchParams.get("state");
    const intent = searchParams.get("intent");

    // ── 초기 진입: 앱이 파라미터와 함께 브라우저를 연 경우 ──────────────────────────
    if (urlCb) {
      if (!isLoopbackCallback(urlCb)) {
        setPhase({
          kind: "error",
          message: "허용되지 않은 콜백 주소입니다. 앱에서 다시 시도해 주세요.",
        });
        return;
      }

      // authRedirect는 pathname만 실어 나르므로 cb·state는 sessionStorage에 따로 보관한다(§6.1).
      sessionStorage.setItem(SS_CB_KEY, urlCb);
      sessionStorage.setItem(SS_STATE_KEY, urlState ?? "");
      // 로그인 화면이 성공 후 이 값을 읽고 자동 복귀한다(Login/index.tsx:61). 제거도 로그인 화면이 한다(:93).
      localStorage.setItem("authRedirect", AUTH_REDIRECT_PATH);

      if (intent === "signup") {
        // 경로 A(신규 가입)는 가입 후 최초 로그인이 한 번 더 필요하다 — 미리 안내해 이탈을 줄인다(§6.1).
        setPhase({ kind: "signupNotice" });
      } else {
        // intent=login(및 미지정 기본): 기존 로그인 화면으로 위임.
        setPhase({ kind: "redirecting" });
        navigate("/login");
      }
      return;
    }

    // ── 복귀/직접 접근: 파라미터 없이 진입 ──────────────────────────────────────────
    const savedCb = sessionStorage.getItem(SS_CB_KEY);
    const savedStateRaw = sessionStorage.getItem(SS_STATE_KEY);
    // 복귀 후 값을 읽고 즉시 삭제한다 — 탭에 남아 다음 방문에 오작동하는 것을 막는다(§6.1).
    sessionStorage.removeItem(SS_CB_KEY);
    sessionStorage.removeItem(SS_STATE_KEY);

    if (savedCb) {
      const savedState = savedStateRaw && savedStateRaw.length > 0 ? savedStateRaw : null;
      runHandoff(savedCb, savedState);
    } else {
      // 파라미터 없는 직접 접근(일반 웹 사용자) → 대시보드로(규칙 5).
      navigate("/dashboard");
    }
  }, [searchParams, navigate, runHandoff]);

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API가 막힌 환경 — 코드는 화면에 선택 가능한 형태로 그대로 노출돼 있으므로 수동 복사 가능.
    }
  };

  const renderBody = () => {
    switch (phase.kind) {
      case "init":
      case "redirecting":
      case "handoff":
        return (
          <>
            <Spinner />
            <Title>PhAI 약국 로그인</Title>
            <Description>
              {phase.kind === "handoff"
                ? "앱과 연결하는 중입니다…"
                : "인증 화면으로 이동하는 중입니다…"}
            </Description>
          </>
        );

      case "signupNotice":
        return (
          <>
            <Title>PhAI 약국 회원가입</Title>
            <Description>
              카카오로 약사 회원가입을 진행합니다.
              <br />
              <Strong>가입 완료 후 한 번 로그인하시면 앱으로 자동 연결됩니다.</Strong>
            </Description>
            <PrimaryButton type="button" onClick={startSignup}>
              카카오로 가입 시작
            </PrimaryButton>
          </>
        );

      case "code":
        return (
          <>
            <Title>앱으로 연결하는 중입니다</Title>
            <Description>
              자동으로 연결되지 않으면 아래 코드를 복사해
              <br />
              PhAI 약국 앱에 붙여넣어 주세요.
            </Description>

            <CodeBox aria-label="로그인 코드">{phase.code}</CodeBox>

            <ButtonRow>
              <PrimaryButton type="button" onClick={() => handleCopy(phase.code)}>
                {copied ? "복사됨" : "코드 복사"}
              </PrimaryButton>
              <SecondaryButton type="button" onClick={() => setIframeKey((k) => k + 1)}>
                다시 연결 시도
              </SecondaryButton>
            </ButtonRow>

            <Hint>이 코드는 60초 후 만료됩니다. 앱에서 로그인이 완료되면 이 창을 닫아도 됩니다.</Hint>

            {/*
              숨김 iframe으로 cb에 code·state를 전달한다(앱의 loopback 수신 서버가 쿼리로 받는다).
              상단 페이지를 유지하므로 loopback이 막혀도 위 코드 폴백이 항상 보인다(§6 규칙 — 필수 폴백).
              iframeKey를 바꾸면 재전송된다.
            */}
            <HiddenFrame
              key={iframeKey}
              src={phase.cbUrl}
              title="desktop-callback"
              sandbox=""
            />
          </>
        );

      case "error":
        return (
          <>
            <Title>연결에 실패했습니다</Title>
            <Description>{phase.message}</Description>
            {phase.onRetry && (
              <PrimaryButton type="button" onClick={phase.onRetry}>
                다시 시도
              </PrimaryButton>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Page>
      <Card>{renderBody()}</Card>
    </Page>
  );
};

export default DesktopAuth;

/* ── styles ─────────────────────────────────────────────────────────────────── */

const Page = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  min-height: 100vh;
  padding: 24px;
  box-sizing: border-box;
  background-color: ${color("Gray", 50)};
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 420px;
  padding: 40px 32px;
  border-radius: 16px;
  background-color: ${color("Gray", 0)};
  box-shadow: 0 8px 24px rgba(15, 32, 52, 0.08);
  text-align: center;
`;

const Title = styled.h1`
  margin: 8px 0 12px;
  font-size: 22px;
  font-weight: 700;
  color: ${color("Gray", 900)};
`;

const Description = styled.p`
  margin: 0 0 24px;
  font-size: 15px;
  line-height: 1.6;
  color: ${color("Gray", 500)};
`;

const Strong = styled.strong`
  color: ${color("Blue", 500)};
  font-weight: 600;
`;

const CodeBox = styled.div`
  width: 100%;
  padding: 20px 16px;
  margin-bottom: 20px;
  border: 1px solid ${color("Gray", 100)};
  border-radius: 12px;
  background-color: ${color("Gray", 10)};
  font-family: "SF Mono", "Menlo", "Consolas", monospace;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 6px;
  color: ${color("Gray", 900)};
  word-break: break-all;
  user-select: all;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  margin-bottom: 16px;
`;

const PrimaryButton = styled.button`
  flex: 1;
  padding: 14px 20px;
  border: none;
  border-radius: 10px;
  background-color: ${color("Blue", 500)};
  color: ${color("Gray", 0)};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${color("Blue", 600)};
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  padding: 14px 20px;
  border: 1px solid ${color("Gray", 100)};
  border-radius: 10px;
  background-color: ${color("Gray", 0)};
  color: ${color("Gray", 700)};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${color("Gray", 10)};
  }
`;

const Hint = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${color("Gray", 400)};
`;

const HiddenFrame = styled.iframe`
  display: none;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  margin-bottom: 20px;
  border: 3px solid ${color("Gray", 100)};
  border-top-color: ${color("Blue", 500)};
  border-radius: 50%;
  animation: desktop-auth-spin 0.8s linear infinite;

  @keyframes desktop-auth-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
